/**
 * RenewDetailsEditor — the OTP step-up flow for editing contact details on the
 * public renew page. Three steps: send a 4-digit code (to whichever channel the
 * member has on file), verify it, then update phone/email. Self-contained bare
 * fetch (public endpoints), styled to match RenewPage.
 */
import React, { useState } from "react";
import {
  Loader2,
  ShieldCheck,
  X,
  Mail,
  Phone,
  CheckCircle2,
} from "lucide-react";

interface Props {
  token: string;
  apiBase: string;
  onClose: () => void;
}

type Step = "send" | "code" | "edit" | "done";

const RenewDetailsEditor: React.FC<Props> = ({ token, apiBase, onClose }) => {
  const [step, setStep] = useState<Step>("send");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [editToken, setEditToken] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const call = async (path: string, opts?: RequestInit) => {
    const res = await fetch(`${apiBase}/renew/${token}${path}`, opts);
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.message || "Something went wrong.");
    return json.data;
  };

  const sendCode = async () => {
    setBusy(true);
    setError("");
    try {
      const d = await call("/request-code", { method: "POST" });
      setSentTo(d.to);
      setStep("code");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError("");
    try {
      const d = await call("/verify-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      });
      setEditToken(d.editToken);
      setStep("edit");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const body: Record<string, string> = { editToken };
      if (phone.trim()) body.phone = phone.trim();
      if (email.trim()) body.email = email.trim();
      if (!body.phone && !body.email) {
        setError("Enter a new phone or email.");
        setBusy(false);
        return;
      }
      await call("/details", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setStep("done");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-[#4CAF50]" />
            <span className="text-sm font-semibold text-slate-900">
              Update your details
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 p-2.5 text-xs text-red-700">
            {error}
          </div>
        )}

        {step === "send" && (
          <>
            <p className="text-sm text-slate-600">
              To keep your account safe, we’ll send a 4-digit code to the phone
              or email on file.
            </p>
            <button
              type="button"
              onClick={sendCode}
              disabled={busy}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#4CAF50] py-3 text-sm font-semibold text-white hover:bg-[#45A049] disabled:opacity-50"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Send code"
              )}
            </button>
          </>
        )}

        {step === "code" && (
          <>
            <p className="text-sm text-slate-600">
              Enter the code we sent to{" "}
              <span className="font-medium text-slate-900">{sentTo}</span>.
            </p>
            <input
              inputMode="numeric"
              maxLength={4}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="••••"
              className="mt-3 w-full rounded-xl border border-slate-200 py-3 text-center text-2xl tracking-[0.5em] outline-none focus:border-[#4CAF50] focus:ring-1 focus:ring-[#4CAF50]"
            />
            <button
              type="button"
              onClick={verify}
              disabled={busy || code.length !== 4}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#4CAF50] py-3 text-sm font-semibold text-white hover:bg-[#45A049] disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
            </button>
            <button
              type="button"
              onClick={sendCode}
              disabled={busy}
              className="mt-2 w-full text-xs text-slate-400 hover:text-slate-600"
            >
              Didn’t get it? Resend
            </button>
          </>
        )}

        {step === "edit" && (
          <>
            <p className="text-sm text-slate-600">
              Enter your new details. Leave a field blank to keep it unchanged.
            </p>
            <div className="mt-3 space-y-2.5">
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3">
                <Phone size={15} className="text-slate-400" />
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="New phone"
                  inputMode="tel"
                  className="w-full py-3 text-sm outline-none"
                />
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-3">
                <Mail size={15} className="text-slate-400" />
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="New email"
                  inputMode="email"
                  className="w-full py-3 text-sm outline-none"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#4CAF50] py-3 text-sm font-semibold text-white hover:bg-[#45A049] disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </button>
          </>
        )}

        {step === "done" && (
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <CheckCircle2 className="h-10 w-10 text-[#4CAF50]" />
            <p className="text-sm font-medium text-slate-900">
              Details updated
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 rounded-xl bg-[#4CAF50] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#45A049]"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RenewDetailsEditor;
